// @flow

import { useSelector } from 'react-redux';
import { isAdministrator } from 'store/selector/UserSelector';
import React from 'react';
import NavigationBar from 'insight/components/navigation/NavigationBar';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import {
  INSIGHT_ADMINISTRATION,
  INSIGHT_ADMINISTRATION_SERVICES,
  INSIGHT_ADMINISTRATION_USERS,
  INSIGHT_ANALYTICS,
  INSIGHT_EDGE,
  INSIGHT_FARMER_APP,
  INSIGHT_FARMER_APP_CUSTOMERS,
  INSIGHT_FARMER_APP_DEVICES,
  INSIGHT_GENERAL,
  INSIGHT_GENERAL_NODES_MAP_MANAGER,
  INSIGHT_GENERAL_OVERVIEW,
  INSIGHT_MANAGER,
  INSIGHT_MANAGER_GATEWAYS,
  INSIGHT_MANAGER_SUBSCRIPTIONS,
  INSIGHT_MANAGER_TENANTS,
  INSIGHT_QUOTE_TOOL,
  INSIGHT_SERVICE_OVERVIEW,
  INSIGHT_SERVICE_STATUS_APP,
  INSIGHT_SERVICE_STATUS_MANAGER
} from 'insight/routing/InsightRouteConstants';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  AdminPanelSettingsOutlined,
  EngineeringOutlined,
  GroupsOutlined,
  HolidayVillageOutlined,
  PeopleOutline,
  PhonelinkRingOutlined,
  VillaOutlined
} from '@mui/icons-material';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import PhonelinkRingOutlinedIcon from '@mui/icons-material/PhonelinkRingOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';

const createSideMenuItems = (
  administrator: boolean,
  adminOpen: boolean,
  generalOpen: boolean,
  serviceOverviewOpen: boolean,
  managerOpen: boolean,
  farmerAppOpen: boolean,
  toggleAdminOpen: Function,
  toggleGeneralOpen: Function,
  toggleManagerOpen: Function,
  toggleFarmerAppOpen: Function,
  toggleServiceOverviewOpen: Function
) => {
  const SideMenuItems = [
    {
      textKey: 'app.navigation.general',
      icon: <TravelExploreOutlinedIcon />,
      path: INSIGHT_GENERAL,
      onClick: toggleGeneralOpen,
      isSubmenuOpen: generalOpen,
      submenu: [
        {
          textKey: 'app.navigation.general.worldMap',
          icon: <MapOutlinedIcon />,
          path: INSIGHT_GENERAL_OVERVIEW
        },
        {
          textKey: 'app.navigation.general.nodesMap',
          icon: <HubOutlinedIcon />,
          path: INSIGHT_GENERAL_NODES_MAP_MANAGER
        }
      ]
    },
    {
      textKey: 'app.navigation.service.overview',
      icon: <InfoOutlinedIcon />,
      path: INSIGHT_SERVICE_OVERVIEW,
      onClick: toggleServiceOverviewOpen,
      isSubmenuOpen: serviceOverviewOpen,
      submenu: [
        {
          textKey: 'app.navigation.service.overview.statusManager',
          icon: <VillaOutlined />,
          path: INSIGHT_SERVICE_STATUS_MANAGER
        },
        {
          textKey: 'app.navigation.service.overview.statusApp',
          icon: <EngineeringOutlined />,
          path: INSIGHT_SERVICE_STATUS_APP
        }
      ]
    },
    {
      textKey: 'app.navigation.manager',
      icon: <VillaOutlined />,
      path: INSIGHT_MANAGER,
      onClick: toggleManagerOpen,
      isSubmenuOpen: managerOpen,
      submenu: [
        {
          textKey: 'app.navigation.manager.tenants',
          icon: <HolidayVillageOutlined />,
          path: INSIGHT_MANAGER_TENANTS
        },
        {
          textKey: 'app.navigation.manager.subscriptions',
          icon: <CardMembershipOutlinedIcon />,
          path: INSIGHT_MANAGER_SUBSCRIPTIONS
        },
        {
          textKey: 'app.navigation.manager.gateways',
          icon: <PhonelinkRingOutlinedIcon />,
          path: INSIGHT_MANAGER_GATEWAYS
        }
      ]
    },
    {
      textKey: 'app.navigation.farmerApp',
      icon: <EngineeringOutlined />,
      path: INSIGHT_FARMER_APP,
      onClick: toggleFarmerAppOpen,
      isSubmenuOpen: farmerAppOpen,
      submenu: [
        {
          textKey: 'app.navigation.farmerApp.customers',
          icon: <GroupsOutlined />,
          path: INSIGHT_FARMER_APP_CUSTOMERS
        },
        {
          textKey: 'app.navigation.farmerApp.devices',
          icon: <PhonelinkRingOutlined />,
          path: INSIGHT_FARMER_APP_DEVICES
        }
      ]
    }
  ];

  if (administrator) {
    const adminNavigationItem = {
      textKey: 'app.navigation.administration',
      icon: <AdminPanelSettingsOutlined />,
      path: INSIGHT_ADMINISTRATION,
      onClick: toggleAdminOpen,
      isSubmenuOpen: adminOpen,
      submenu: [
        {
          textKey: 'app.navigation.administration.users',
          icon: <PeopleOutline />,
          path: INSIGHT_ADMINISTRATION_USERS
        },
        {
          textKey: 'app.navigation.administration.services',
          icon: <SettingsSuggestOutlinedIcon />,
          path: INSIGHT_ADMINISTRATION_SERVICES
        }
      ]
    };

    SideMenuItems.push(adminNavigationItem);
  }

  SideMenuItems.push(
    {
      textKey: 'app.navigation.analytics',
      icon: <QueryStatsOutlinedIcon />,
      path: INSIGHT_ANALYTICS
    },
    {
      textKey: 'app.navigation.edge',
      icon: <TravelExploreOutlinedIcon />,
      path: INSIGHT_EDGE
    },
    {
      textKey: 'app.navigation.quoteTool',
      icon: <PointOfSaleOutlinedIcon />,
      path: INSIGHT_QUOTE_TOOL
    }
  );

  return SideMenuItems;
};

const InsightNavigationBar = ({ children }: any) => {
  const administrator = useSelector(isAdministrator);
  const [adminOpen, setAdminOpen] = React.useState(false);
  const [generalOpen, setGeneralOpen] = React.useState(false);
  const [managerOpen, setManagerOpen] = React.useState(false);
  const [farmerAppOpen, setFarmerAppOpen] = React.useState(false);
  const [serviceOverviewOpen, setServiceOverviewOpen] = React.useState(false);

  const toggleAdminOpen = () => {
    setAdminOpen(!adminOpen);
  };
  const toggleGeneralOpen = () => {
    setGeneralOpen(!generalOpen);
  };
  const toggleManagerOpen = () => {
    setManagerOpen(!managerOpen);
  };
  const toggleFarmerAppOpen = () => {
    setFarmerAppOpen(!farmerAppOpen);
  };
  const toggleServiceOverviewOpen = () => {
    setServiceOverviewOpen(!serviceOverviewOpen);
  };

  let SideMenuItems = createSideMenuItems(
    administrator,
    adminOpen,
    generalOpen,
    serviceOverviewOpen,
    managerOpen,
    farmerAppOpen,
    toggleAdminOpen,
    toggleGeneralOpen,
    toggleManagerOpen,
    toggleFarmerAppOpen,
    toggleServiceOverviewOpen
  );

  return <NavigationBar SideMenuItems={SideMenuItems}>{children}</NavigationBar>;
};

export default InsightNavigationBar;
